import { asGroupedFieldOptionsForFilter } from 'module/helpers/filter/grouped-field-transformer';
import { useGroupFilters } from 'module/hooks/filters/use-group-filters';
import { useTranslate } from 'module/hooks/use-translation';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { GroupedOptions } from 'module/types/filter-field/field/grouped-field';
import { SelectFilterOperationType } from 'module/types/filter-field/filter/select';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { GroupFieldsMeta } from 'module/ui/components/molecules/field-filter/filter-transformation/filter-transformer';
import { SignalType } from 'api/signal/types';
import { useCompanyFieldsAsFilterableField } from 'helpers/fields/company-fields/fields';
import { useSignalFieldsAsFilterableField } from 'helpers/fields/signal-fields/fields';
import { useCallback, useState } from 'react';
import { SignalConfigurationType } from 'components/pages/accounts/detail/all-signals/all-signals-content';
import { useFilterState } from './use-filter-state';

export interface SignalFilterParams {
  signalType?: SignalType;
  myAccountSignalType?: SignalType;
  selectedSignals: string[];
}

export const useAllSignalsState = () => {
  const { fields: companyFields } = useCompanyFieldsAsFilterableField();
  const { fields: signalFields } = useSignalFieldsAsFilterableField();

  const groupedFields = asGroupedFieldOptionsForFilter(
    {
      [FilterGroupType.Company]: {
        fields: companyFields,
      },
      [FilterGroupType.Signal]: {
        fields: signalFields,
      },
    },
    useTranslate(),
  );

  const [value, setValue] = useState('');
  const [current, setCurrent] = useState('EVERYTHING');
  const [myAccountsOpen, setMyAccountsOpen] = useState(true);
  const [savedViewsOpen, setSavedViewsOpen] = useState(true);
  const [sharedViewsOpen, setSharedViewsOpen] = useState(true);
  const [savedViewsSelected, setSavedViewsSelected] = useState('');
  const [sharedViewsSelected, setSharedViewsSelected] = useState('');
  const [selectedSignalUnderMyAccounts, setSelectedSignalUnderMyAccounts] =
    useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const {
    filter: groupFilter,
    replaceFilter: _replaceGroupFilter,
    signalFilterParams: groupSignalFilterParams,
    setSignalFilterParams: _setGroupSignalFilterParams,
  } = useGroupFilters({
    groupFieldsMeta: groupedFields as unknown as GroupFieldsMeta,
    defaultGroup: FilterGroupType.Company,
    canSetFilter: true,
  });

  const {
    filter,
    selectedSignals,
    signalFilterParams,
    updateFilter,
    updateSelectedSignals,
    updateSignalFilterParams,
    resetFilter,
  } = useFilterState(groupFilter, [], {
    signalType: groupSignalFilterParams.signalType as SignalType,
    myAccountSignalType:
      groupSignalFilterParams.myAccountSignalType as SignalType,
    selectedSignals: groupSignalFilterParams.selectedSignals || [],
  });

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  const clear = useCallback(() => {
    setValue('');
  }, []);

  const resetViewSelected = useCallback(() => {
    setSavedViewsSelected('');
    setSharedViewsSelected('');
    resetFilter();
  }, [resetFilter]);

  const createSignalTypeFilter = useCallback(
    (signalType: string) => {
      const signalTypeField = signalFields?.find(
        (field: any) => field.fieldName === 'signalType',
      );

      if (signalTypeField) {
        const signalTypeCriteria = {
          id: FilterGroupType.Signal,
          criteria: {
            field: signalTypeField,
            type: SelectFilterOperationType.Is,
            value: signalType,
          } as any,
        };

        const newFilter = {
          ...filter,
          groupCriteria: [signalTypeCriteria],
        };

        updateFilter(newFilter);
      }
    },
    [signalFields, filter, updateFilter],
  );

  const handleSelect = useCallback(
    (key: string) => {
      setSelectedSignalUnderMyAccounts('');
      resetViewSelected();
      setCurrent(key);
      updateSignalFilterParams({
        ...signalFilterParams,
        myAccountSignalType: undefined,
        signalType:
          key === SignalConfigurationType.EVERYTHING
            ? undefined
            : (key as SignalType),
      });
      if (key !== SignalConfigurationType.EVERYTHING) {
        createSignalTypeFilter(key);
      }
    },
    [
      resetViewSelected,
      updateSignalFilterParams,
      signalFilterParams,
      createSignalTypeFilter,
    ],
  );

  const handleMyAccountSignalSelect = useCallback(
    (type: string) => {
      setCurrent('');
      resetViewSelected();
      setSelectedSignalUnderMyAccounts(type);
      updateSignalFilterParams({
        ...signalFilterParams,
        myAccountSignalType: type as SignalType,
        signalType: undefined,
      });
      createSignalTypeFilter(type);
    },
    [
      resetViewSelected,
      updateSignalFilterParams,
      signalFilterParams,
      createSignalTypeFilter,
    ],
  );

  const onFilterChange = useCallback(
    (newFilter: any) => {
      updateFilter(newFilter);
    },
    [updateFilter],
  );

  const onDone = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }, []);

  const onCancel = useCallback(() => {
    resetFilter();
  }, [resetFilter]);

  const setSelectedSignalsAndParams = useCallback(
    (signals: string[]) => {
      updateSelectedSignals(signals);
      // Note: Signal selection is now handled directly in the filter
      // No need to update signalFilterParams separately
    },
    [updateSelectedSignals],
  );

  return {
    // State
    value,
    current,
    myAccountsOpen,
    savedViewsOpen,
    sharedViewsOpen,
    savedViewsSelected,
    sharedViewsSelected,
    selectedSignalUnderMyAccounts,
    selectedSignals,
    isSidebarOpen,
    filter,
    signalFilterParams,

    // Setters
    setValue,
    setCurrent,
    setMyAccountsOpen,
    setSavedViewsOpen,
    setSharedViewsOpen,
    setSavedViewsSelected,
    setSharedViewsSelected,
    setSelectedSignalUnderMyAccounts,
    setSelectedSignals: setSelectedSignalsAndParams,
    setIsSidebarOpen,

    // Actions
    onChange,
    clear,
    handleSelect,
    handleMyAccountSignalSelect,
    onFilterChange,
    onDone,
    onCancel,
    replaceFilter: updateFilter,
    resetFilter,

    // Data
    groupedFields:
      groupedFields as unknown as GroupedOptions<FilterableField>[],
  };
};
